import React, { useEffect, useState } from "react";
import { Webhook, RefreshCw, AlertTriangle, Trash, Play } from "lucide-react";
import { webhooksApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

const STATUS_COLOR = {
  active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  disabled_by_system: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};

function StatusPill({ status }) {
  return <span className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${STATUS_COLOR[status] || "border-border bg-muted/30 text-muted-foreground"}`}>{status?.replace(/_/g, " ")}</span>;
}

export default function AdminWebhooks() {
  const [metrics, setMetrics] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const [m, l] = await Promise.all([webhooksApi.adminMetrics(), webhooksApi.adminListAll()]);
      setMetrics(m);
      setSubs(l.subscriptions || []);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reEnable = async (s) => {
    if (!window.confirm(`Re-enable ${s.url}?`)) return;
    try {
      await webhooksApi.adminOverrideStatus(s.id, "active");
      toast.success("Re-enabled");
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const prune = async () => {
    if (!window.confirm("Delete all delivery rows older than 30 days?")) return;
    try {
      const r = await webhooksApi.adminPrune();
      toast.success(`Pruned ${r.deleted} rows`);
      load();
    } catch (e) {
      toast.error("Prune failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const filtered = filter ? subs.filter((s) => s.status === filter) : subs;

  return (
    <div className="max-w-7xl px-6 md:px-10 py-10 md:py-14" data-testid="admin-webhooks">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Super-admin</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium">Webhooks fleet</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="admin-webhooks-refresh"><RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh</button>
          <button onClick={prune} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="admin-webhooks-prune"><Trash className="w-3 h-3" strokeWidth={1.5} /> Prune deliveries</button>
        </div>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-webhooks-metrics">
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total subs</div>
            <div className="text-2xl font-mono mt-1">{metrics.subscriptions?.total || 0}</div>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Active</div>
            <div className="text-2xl font-mono mt-1 text-status-ok">{metrics.subscriptions?.active || 0}</div>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Auto-disabled</div>
            <div className="text-2xl font-mono mt-1 text-status-tampered">{metrics.subscriptions?.disabled_by_system || 0}</div>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">P95 latency 24h</div>
            <div className="text-2xl font-mono mt-1">{metrics.p95_latency_ms || 0}<span className="text-sm text-muted-foreground">ms</span></div>
          </div>
        </div>
      )}

      {metrics?.deliveries_24h && (
        <div className="border border-border bg-card rounded-sm p-4 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Deliveries in 24h</div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(metrics.deliveries_24h).map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-2 text-sm">
                <span className="font-mono">{v}</span>
                <span className="text-[11px] text-muted-foreground uppercase tracking-widest">{k}</span>
              </div>
            ))}
            {Object.keys(metrics.deliveries_24h).length === 0 && <span className="text-xs text-muted-foreground">No deliveries in the last 24h.</span>}
          </div>
        </div>
      )}

      {metrics?.top_failing_subscriptions?.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2 text-amber-700 dark:text-amber-400"><AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> Top failing subscriptions (24h)</div>
          <div className="space-y-1 text-xs">
            {metrics.top_failing_subscriptions.map((t) => (
              <div key={t.subscription_id} className="font-mono">{t.subscription_id} — {t.failures} failed</div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-3">
        {["", "active", "paused", "disabled_by_system"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            className={`text-[11px] uppercase tracking-widest px-3 py-1.5 border rounded-sm ${filter === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}
            data-testid={`admin-webhooks-filter-${s || "all"}`}
          >{s ? s.replace(/_/g, " ") : "all"}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-webhooks-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-3 font-normal">Business</th>
                <th className="text-left p-3 font-normal">URL</th>
                <th className="text-left p-3 font-normal">Status</th>
                <th className="text-left p-3 font-normal">Failures</th>
                <th className="text-left p-3 font-normal">Last delivery</th>
                <th className="text-right p-3 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-border" data-testid={`admin-webhook-row-${s.id}`}>
                  <td className="p-3 font-mono text-[11px] text-muted-foreground">{s.business_id}</td>
                  <td className="p-3 font-mono text-xs max-w-md truncate">{s.url}</td>
                  <td className="p-3"><StatusPill status={s.status} /></td>
                  <td className="p-3 text-xs">{s.consecutive_failure_count || 0}</td>
                  <td className="p-3 text-[11px] text-muted-foreground">{s.last_delivery_at ? new Date(s.last_delivery_at).toLocaleString() : "—"}</td>
                  <td className="p-3 text-right">
                    {s.status === "disabled_by_system" && (
                      <button onClick={() => reEnable(s)} className="text-[11px] border border-border px-2 py-1 rounded-sm hover:border-foreground inline-flex items-center gap-1" data-testid={`admin-webhook-reenable-${s.id}`}>
                        <Play className="w-3 h-3" strokeWidth={1.5} /> Re-enable
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
